import { useNavigate, createSearchParams } from 'react-router-dom'
import { DialogActions, DialogContent } from '@mui/material'
import React, { useState, useEffect } from 'react'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { isAxiosError } from 'axios'
import Box from '@mui/material/Box'

import ContainedButton from 'shared/components/Buttons/ContainedButton'
import swapUserLocationService from 'shared/services/swapUserLocation'
import { useSetActiveCartMutation } from 'shared/store/useSavedCarts'
import { useNotificationContext } from 'shared/context/Notifications'
import { getRefreshToken } from 'shared/utils/helpers/refreshToken'
import invalidateCarts from 'shared/utils/helpers/invalidateCarts'
import { useProfileQuery } from 'shared/store/useProfile'

import { NotificationInfo, ParsedMessageType } from '../../../../types'

interface Props {
   type: PropType<NotificationInfo, 'type'>
   message: string
   notificationLocationUuid?: string
   cartUuid: string
   handleClose: () => void
}

export default function CsvSummaryNotification({
   type,
   notificationLocationUuid,
   message,
   cartUuid,
   handleClose,
}: Props) {
   const { t } = useTranslation('NOTIFICATIONS')
   const [parsedMessage, setParsedMessage] = useState<ParsedMessageType>()
   const [notFoundIndexes, setNotFoundIndexes] = useState<string[]>([])
   const { mutate } = useSetActiveCartMutation()
   const navigate = useNavigate()
   const { data: profile } = useProfileQuery()
   const queryClient = useQueryClient()
   const { addNotification } = useNotificationContext()

   function handleClick() {
      if (!notificationLocationUuid || notificationLocationUuid === profile?.locationUuid) {
         mutate(cartUuid, {
            onSuccess: () => navigate('/dashboard/main-cart'),
         })
      } else if (notificationLocationUuid !== profile?.locationUuid) {
         if (profile?.availableLocations.find((item) => item.uuid === notificationLocationUuid)) {
            handleLocationSwap(notificationLocationUuid, 'CART')
         } else {
            addNotification(t('NOTIFICATIONS:locationPermissionError'), 'error')
         }
      }
   }

   function handleLocationSwap(locationUuid: string, variant: 'SEARCH' | 'CART') {
      swapUserLocationService(locationUuid)
         .then((res) => {
            if (res.status === 200) {
               getRefreshToken(queryClient)
               queryClient.invalidateQueries('userProfile')
               queryClient.invalidateQueries('userStats')
               invalidateCarts(queryClient)
            }
         })
         .then(() => {
            mutate(cartUuid, {
               onSuccess: () => {
                  if (variant === 'CART') {
                     navigate('/dashboard/main-cart')
                  } else {
                     navigate(
                        {
                           pathname: '/dashboard/search',
                           search: `?${createSearchParams({
                              value: notFoundIndexes[0],
                              searchType: 'INDEX',
                           })}`,
                        },
                        {
                           state: { indexes: notFoundIndexes, activeIndex: 0 },
                        }
                     )
                  }
               },
            })
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               addNotification(
                  error.response?.data?.errors[0].code || t('AUTHORIZED:header.failedLocationSwap'),
                  'error'
               )
            }
         })
   }

   function handleSearch() {
      if (!notificationLocationUuid || notificationLocationUuid === profile?.locationUuid) {
         mutate(cartUuid, {
            onSuccess: () => {
               navigate(
                  {
                     pathname: '/dashboard/search',
                     search: `?${createSearchParams({
                        value: notFoundIndexes[0],
                        searchType: 'INDEX',
                     })}`,
                  },
                  {
                     state: { indexes: notFoundIndexes, activeIndex: 0 },
                  }
               )
            },
         })
      } else if (notificationLocationUuid !== profile?.locationUuid) {
         if (profile?.availableLocations.find((item) => item.uuid === notificationLocationUuid)) {
            handleLocationSwap(notificationLocationUuid, 'SEARCH')
         } else {
            addNotification(t('NOTIFICATIONS:locationPermissionError'), 'error')
         }
      }
   }

   useEffect(() => {
      if (message) {
         setParsedMessage(JSON.parse(message))
      }
   }, [message])

   useEffect(() => {
      if (parsedMessage) {
         const tempNotFoundIndexes = [...parsedMessage.rejectedIndexes.split(',')]
         if (Object.hasOwn(parsedMessage, 'toSpecifyIndexes')) {
            parsedMessage.toSpecifyIndexes.split(',').map((item) => {
               return tempNotFoundIndexes.push(item)
            })
         }
         setNotFoundIndexes(tempNotFoundIndexes.filter((item) => item))
      }
   }, [parsedMessage])

   return (
      <>
         <DialogContent>
            <div style={{ textAlign: 'center' }}>
               {type === 'AUTO_CART_CREATED' ? (
                  <Typography variant="h5">{t('cartCreatedText')}</Typography>
               ) : (
                  <Typography variant="h5">{t('cartCreatedFailedText')}</Typography>
               )}
               {type === 'AUTO_CART_CREATED' && (
                  <Typography
                     sx={{ mt: 0.5, '&:hover': { cursor: 'pointer', color: 'primary.dark' } }}
                     onClick={handleClick}
                     color="primary"
                     variant="h5"
                  >
                     {t('goToCart')}
                  </Typography>
               )}
               {parsedMessage &&
               (parsedMessage.notFoundIndexes || parsedMessage.rejectedIndexes || parsedMessage.toSpecifyIndexes) ? (
                  <Box>
                     <Typography variant="h6" sx={{ mt: 2 }}>
                        {t('notFoundProducts', {
                           rejectedCount: parsedMessage.numberOfRejectedIndexes || '-',
                           notFoundCount: parsedMessage.numberOfNotFoundIndexes || '-',
                           toSpecifyCount: parsedMessage.numberOfToSpecifyIndexes || '-',
                        })}
                     </Typography>
                     <Typography
                        sx={{ '&:hover': { cursor: 'pointer', color: 'primary.dark' } }}
                        onClick={handleSearch}
                        color="primary"
                        variant="h5"
                     >
                        {t('notFoundProductsSearch')}
                     </Typography>
                     <Box sx={{ maxHeight: 250, overflowX: 'scroll', my: 3 }}>
                        {parsedMessage.notFoundIndexes &&
                           parsedMessage.notFoundIndexes?.split(',').map((item) => (
                              <Typography key={item} variant="h6">
                                 {`${item} ${t('notFoundIndex')}`}
                              </Typography>
                           ))}
                        {parsedMessage.rejectedIndexes &&
                           parsedMessage.rejectedIndexes?.split(',').map((item) => (
                              <Typography key={item} variant="h6">
                                 {`${item} ${t('rejectedIndex')}`}
                              </Typography>
                           ))}
                        {parsedMessage.toSpecifyIndexes &&
                           parsedMessage.toSpecifyIndexes?.split(',').map((item) => (
                              <Typography key={item} variant="h6">
                                 {`${item} ${t('toSpecifyIndexes')}`}
                              </Typography>
                           ))}
                     </Box>
                  </Box>
               ) : null}
            </div>
         </DialogContent>
         <DialogActions>
            <ContainedButton variant="contained" onClick={handleClose}>
               OK
            </ContainedButton>
         </DialogActions>
      </>
   )
}
